import {
  QueryObserverResult,
  QueryStatus,
  keepPreviousData as keepPreviousDataFn,
  useQuery,
} from '@tanstack/react-query';

import {
  EndpointCall,
  MonolithMethodNames,
  MonolithMethods,
  callEndpoint,
} from '../callEndpoint';

type Falsy = false | 0 | -0 | '' | null | undefined;

export type UseMonolithQueryProps<T extends MonolithMethodNames> = Omit<
  EndpointCall<T>,
  'params'
> & {
  params?: EndpointCall<T>['params'] | Falsy;
  enabled?: boolean;
  refetchInterval?: number | false;
  /**
   * Keep showing the previous query's data while a new query (e.g. after a
   * filter/sort/page change) loads, instead of dropping to `undefined`. Keeps
   * consumers that unmount on missing data (like table skeletons) mounted
   * across refetches. Off by default.
   */
  keepPreviousData?: boolean;
};

type UseMonolithQueryReturnType<T extends MonolithMethodNames> = {
  isLoading: boolean;
  isFetching: boolean;
  isRefetching: boolean;
  isPlaceholderData: boolean;
  isError: boolean;
  dataUpdatedAt: number;
  errorUpdatedAt: number;
  refetch: () => Promise<
    QueryObserverResult<Awaited<ReturnType<MonolithMethods[T]>> | undefined>
  >;
  data?: Awaited<ReturnType<MonolithMethods[T]>>;
  error?: unknown;
  status: QueryStatus;
};

export function useMonolithQuery<T extends MonolithMethodNames>({
  method,
  params,
  anonymous,
  enabled = true,
  refetchInterval,
  keepPreviousData = false,
}: UseMonolithQueryProps<T>): UseMonolithQueryReturnType<T> {
  const {
    isLoading,
    isFetching,
    isRefetching,
    isPlaceholderData,
    isError,
    dataUpdatedAt,
    errorUpdatedAt,
    refetch,
    data,
    error,
    status,
  } = useQuery({
    queryKey: [method, ...(params || [])],
    queryFn: async () => {
      if (params) {
        return await callEndpoint({ method, params, anonymous });
      }
      return null;
    },
    enabled,
    refetchInterval,
    placeholderData: keepPreviousData ? keepPreviousDataFn : undefined,
  });

  return {
    isLoading,
    isFetching,
    isRefetching,
    isPlaceholderData,
    isError,
    dataUpdatedAt,
    errorUpdatedAt,
    refetch,
    data: data ?? undefined,
    error,
    status,
  } as UseMonolithQueryReturnType<T>;
}
